import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";

import { ErrorAlert } from "@/components/ui/errorAlert";
import { Loader } from "@/components/ui/loader";
import { useGetBusinessReportsQuery, useGetTemplateFavoritesQuery } from "@/modules/snapshots/businessReports.api";
import type { BusinessReport } from "@/modules/snapshots/businessReports.types";
import { buildSnapshotGridPath } from "@/modules/snapshots/routes";
import { useGetSnapshotCollectionsQuery } from "@/modules/snapshots/snapshotCollection.api";
import type { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";

import { SnapshotCollectionsDashboard } from "./components/dashboard";
import {
  buildCollectionRows,
  buildTemplateNavigatorModel,
  filterCollectionRows,
  filterSidebarCollections,
  getDashboardSummary,
  resolveSnapshotCollectionsRouteFocus,
} from "./dashboardUtils";

const emptyTemplateList: BusinessReport[] = [];
const emptyCollectionList: SnapshotCollection[] = [];
const BUSINESS_REPORT_QUERY_KEY = "businessReportId";
const COLLECTION_QUERY_KEY = "snapshotCollectionId";
const REFRESH_QUERY_KEY = "refresh";

export const SnapshotCollections: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarSearch, setSidebarSearch] = React.useState("");
  const [collectionQuery, setCollectionQuery] = React.useState("");
  const appliedRouteFocusKeyRef = React.useRef<string | null>(null);

  const {
    data: templates = emptyTemplateList,
    error: templatesError,
    isLoading: isLoadingTemplates,
  } = useGetBusinessReportsQuery();
  const { data: favorites = [], error: favoritesError, isLoading: isLoadingFavorites } = useGetTemplateFavoritesQuery();
  const {
    data: collections = emptyCollectionList,
    error: collectionsError,
    isLoading: isLoadingCollections,
    refetch: refetchCollections,
  } = useGetSnapshotCollectionsQuery({ exclude_pipeline_managed: true });

  const businessReportId = searchParams.get(BUSINESS_REPORT_QUERY_KEY);
  const snapshotCollectionId = searchParams.get(COLLECTION_QUERY_KEY);
  const refreshParam = searchParams.get(REFRESH_QUERY_KEY);

  const favoriteTemplateIds = React.useMemo(
    () => new Set(favorites.map(favorite => favorite.business_report_id)),
    [favorites],
  );
  const summary = React.useMemo(() => getDashboardSummary(collections), [collections]);
  const sidebarCollections = React.useMemo(
    () => filterSidebarCollections(collections, templates, sidebarSearch),
    [collections, sidebarSearch, templates],
  );
  const navigatorModel = React.useMemo(
    () =>
      buildTemplateNavigatorModel({
        templates,
        collections: sidebarCollections,
        allCollections: collections,
        emptyTemplateQuery: sidebarSearch,
        favoriteTemplateIds,
        segment: "pinned",
      }),
    [collections, favoriteTemplateIds, sidebarCollections, sidebarSearch, templates],
  );
  const collectionRows = React.useMemo(() => buildCollectionRows(collections, templates), [collections, templates]);
  const filteredRows = React.useMemo(
    () => filterCollectionRows(collectionRows, collectionQuery),
    [collectionQuery, collectionRows],
  );
  const routeFocus = React.useMemo(
    () =>
      resolveSnapshotCollectionsRouteFocus({
        businessReportId,
        snapshotCollectionId,
        collections,
        templates,
      }),
    [businessReportId, collections, snapshotCollectionId, templates],
  );
  const selectedCollection = React.useMemo(
    () => collections.find(collection => collection.id === snapshotCollectionId),
    [collections, snapshotCollectionId],
  );

  const handleOpenCollection = React.useCallback(
    (collection: SnapshotCollection) => {
      navigate(buildSnapshotGridPath(collection.business_report_id, collection.id));
    },
    [navigate],
  );

  const handleOpenSubcollection = React.useCallback(
    (collection: SnapshotCollection, subcollectionId: string) => {
      navigate(buildSnapshotGridPath(collection.business_report_id, collection.id, { subcollectionId }));
    },
    [navigate],
  );

  React.useEffect(() => {
    if (refreshParam !== "true") return;

    const timer = window.setTimeout(() => {
      void refetchCollections();
      setSearchParams(currentSearchParams => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        nextSearchParams.delete(REFRESH_QUERY_KEY);
        return nextSearchParams;
      });
    }, 2000);

    return () => window.clearTimeout(timer);
  }, [refreshParam, refetchCollections, setSearchParams]);

  React.useEffect(() => {
    if (!routeFocus) {
      appliedRouteFocusKeyRef.current = null;
      return;
    }

    if (appliedRouteFocusKeyRef.current === routeFocus.key) return;

    appliedRouteFocusKeyRef.current = routeFocus.key;
    setCollectionQuery(routeFocus.query);
    setSidebarSearch(routeFocus.query);
  }, [routeFocus]);

  const error = templatesError || favoritesError || collectionsError;
  if (error) {
    return <ErrorAlert title="Unable to load collections" error={error} className="p-4" />;
  }

  if (isLoadingTemplates || isLoadingFavorites || isLoadingCollections) {
    return <Loader />;
  }

  return (
    <SnapshotCollectionsDashboard
      activeCollectionId={snapshotCollectionId ?? undefined}
      collectionQuery={collectionQuery}
      navigatorModel={navigatorModel}
      rows={filteredRows}
      selectedCollection={selectedCollection}
      sidebarSearch={sidebarSearch}
      summary={summary}
      onCollectionQueryChange={setCollectionQuery}
      onOpenCollection={handleOpenCollection}
      onOpenSubcollection={handleOpenSubcollection}
      onSidebarSearchChange={setSidebarSearch}
    />
  );
};
